import { AvailabilityTimeFrame, type AvailabilityRule } from '@kouto/types';
import { useCallback, useMemo } from 'react';
import { Session } from 'types/listings';
import {
  deriveFlexibleDurationOptions,
  deriveFlexibleStartOptions,
  isDayBasedTimeFrame,
  type ContinuousSelection,
} from 'utils/continuousSelection';

interface UseContinuousOptionsParams {
  date?: string | null;
  /** The set lengths the group is sold in; when present, the only lengths on offer. */
  durations?: readonly { durationMinutes: number }[];
  experienceId?: string;
  /** Where a picked start and length go: the URL, by way of the page's own handler. */
  onChange: (time: string | undefined, duration: number) => void;
  rule?: AvailabilityRule | null;
  selection: ContinuousSelection | null;
  spans: readonly Session[];
  timeFrame?: AvailabilityTimeFrame | null;
}

interface ContinuousOptions {
  /** The lengths offered from the chosen start; empty until one is chosen. */
  durationOptions: readonly number[];
  onDurationSelect: (duration: number) => void;
  onStartSelect: (time: string) => void;
  /** The starts offered on the date; empty for a day-based resource, which picks dates instead. */
  startOptions: readonly string[];
}

const NO_OPTIONS: readonly never[] = [];

/**
 * What a guest may pick for a continuously booked resource on the date shown, and what picking it
 * means.
 *
 * Lives beside the page rather than inside the picker so the picker only renders: which starts are
 * free, which lengths fit inside the span a start sits in, and the rule that a start already chosen
 * keeps its length when the new start still has room for it, are all questions about availability.
 * A day-based resource offers neither — it picks whole dates on a calendar — so both lists are empty.
 */
const useContinuousOptions = ({
  date,
  durations,
  experienceId,
  onChange,
  rule,
  selection,
  spans,
  timeFrame,
}: UseContinuousOptionsParams): ContinuousOptions => {
  const isFlexible = Boolean(
    date && rule && timeFrame && !isDayBasedTimeFrame(timeFrame),
  );
  const selectedTime = selection?.startDateTime.split('T')[1];
  const selectedDuration = selection?.durationMinutes;

  const startOptions = useMemo(
    () =>
      isFlexible && date && rule
        ? deriveFlexibleStartOptions({ date, experienceId, rule, spans })
        : NO_OPTIONS,
    [date, experienceId, isFlexible, rule, spans],
  );

  // The lengths that fit from `startTime`, whether that start is the chosen one or a candidate.
  const lengthsFrom = useCallback(
    (startTime: string): readonly number[] =>
      isFlexible && date && rule
        ? deriveFlexibleDurationOptions({
            date,
            durations,
            experienceId,
            rule,
            spans,
            startTime,
          })
        : NO_OPTIONS,
    [date, durations, experienceId, isFlexible, rule, spans],
  );

  const durationOptions = useMemo(
    () => (selectedTime ? lengthsFrom(selectedTime) : NO_OPTIONS),
    [lengthsFrom, selectedTime],
  );

  const onStartSelect = useCallback(
    (time: string) => {
      // Keep the length when it still fits from the new start, else offer the shortest that does.
      const lengths = lengthsFrom(time);
      const duration =
        selectedDuration && lengths.includes(selectedDuration)
          ? selectedDuration
          : lengths[0];
      if (duration) {
        onChange(time, duration);
      }
    },
    [lengthsFrom, onChange, selectedDuration],
  );

  const onDurationSelect = useCallback(
    (duration: number) => {
      if (selectedTime) {
        onChange(selectedTime, duration);
      }
    },
    [onChange, selectedTime],
  );

  return { durationOptions, onDurationSelect, onStartSelect, startOptions };
};

export default useContinuousOptions;
